/**
 * Skills Tab Component
 * Tab for managing bot skills in BotModal
 */

import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Popconfirm,
  message,
  Select,
  Modal,
  Form,
  InputNumber,
  Switch,
  Typography,
  Alert,
} from 'antd';
import { PlusOutlined, DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useLanguage } from '../../../hooks/useLanguage';
import { SkillsApi } from '../services/skillsApi';
import type { Skill, ChatbotSkill, AssignSkillRequest } from '../types';

const { Text } = Typography;

interface SkillsTabProps {
  chatbotId?: string; // Undefined if creating new bot
  onChange?: (skills: ChatbotSkill[]) => void;
}

export const SkillsTab: React.FC<SkillsTabProps> = ({ chatbotId, onChange }) => {
  const { t } = useLanguage();
  const [availableSkills, setAvailableSkills] = useState<Skill[]>([]);
  const [assignedSkills, setAssignedSkills] = useState<ChatbotSkill[]>([]);
  const [loading, setLoading] = useState(false);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [selectedSkillId, setSelectedSkillId] = useState<string>();
  const [_selectedChatbotSkill, setSelectedChatbotSkill] = useState<ChatbotSkill>();
  const [assignForm] = Form.useForm();
  const [configForm] = Form.useForm();

  useEffect(() => {
    loadAvailableSkills();
    if (chatbotId) {
      loadAssignedSkills();
    }
  }, [chatbotId]);

  const loadAvailableSkills = async () => {
    try {
      const data = await SkillsApi.listSkills({ is_active: true });
      setAvailableSkills(data.skills);
    } catch (error) {
      console.error('Error loading skills:', error);
    }
  };

  const loadAssignedSkills = async () => {
    if (!chatbotId) return;

    setLoading(true);
    try {
      const skills = await SkillsApi.getChatbotSkills(chatbotId, true);
      setAssignedSkills(skills);
      if (onChange) {
        onChange(skills);
      }
    } catch (error) {
      console.error('Error loading assigned skills:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAssignModal = () => {
    assignForm.resetFields();
    assignForm.setFieldsValue({
      skill_priority: 1,
      min_user_tier: 1,
      is_enabled: true,
    });
    setAssignModalVisible(true);
  };

  const handleAssignSkill = async () => {
    if (!chatbotId) {
      message.warning(t('skills.saveBotFirst') || 'Please save the bot first before assigning skills');
      setAssignModalVisible(false);
      return;
    }

    try {
      const values = await assignForm.validateFields();
      const data: AssignSkillRequest = {
        skill_id: selectedSkillId!,
        skill_priority: values.skill_priority,
        min_user_tier: values.min_user_tier,
        is_enabled: values.is_enabled,
      };

      await SkillsApi.assignSkillToChatbot(chatbotId, data);
      message.success(t('skills.assignSuccess') || 'Skill assigned successfully');
      setAssignModalVisible(false);
      loadAssignedSkills();
    } catch (error: any) {
      message.error(t('skills.assignError') || 'Failed to assign skill');
      console.error('Error assigning skill:', error);
    }
  };

  const handleUnassignSkill = async (skillId: string) => {
    if (!chatbotId) return;

    try {
      await SkillsApi.unassignSkillFromChatbot(chatbotId, skillId);
      message.success(t('skills.unassignSuccess') || 'Skill unassigned successfully');
      loadAssignedSkills();
    } catch (error) {
      message.error(t('skills.unassignError') || 'Failed to unassign skill');
      console.error('Error unassigning skill:', error);
    }
  };

  const handleOpenConfigModal = (chatbotSkill: ChatbotSkill) => {
    setSelectedChatbotSkill(chatbotSkill);
    configForm.setFieldsValue({
      skill_priority: chatbotSkill.skill_priority,
      min_user_tier: chatbotSkill.min_user_tier,
      is_enabled: chatbotSkill.is_enabled,
    });
    setConfigModalVisible(true);
  };

  const handleUpdateConfig = async () => {
    // Note: This would require an update endpoint for chatbot_skills
    // For now, we'll just close the modal
    message.info(t('skills.configUpdateNotImplemented') || 'Configuration update will be implemented');
    setConfigModalVisible(false);
  };

  const columns: ColumnsType<ChatbotSkill> = [
    {
      title: t('skills.name') || 'Skill',
      key: 'skill_name',
      render: (_: any, record: ChatbotSkill) => {
        const skill = availableSkills.find(s => s.skill_id === record.skill_id) || record.skill;
        const name = skill?.skill_name || t('bots.unknownSkill');
        const desc = skill?.skill_description || '';
        return (
          <Space direction="vertical" size={0}>
            <Text strong>{name}</Text>
            {desc && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {desc.substring(0, 60)}
                {desc.length > 60 ? '...' : ''}
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: t('skills.priority') || 'Priority',
      dataIndex: 'skill_priority',
      key: 'skill_priority',
      width: 100,
      render: (priority: number) => <Tag color="blue">{priority}</Tag>,
      sorter: (a, b) => a.skill_priority - b.skill_priority,
    },
    {
      title: t('skills.minTier') || 'Min Tier',
      dataIndex: 'min_user_tier',
      key: 'min_user_tier',
      width: 100,
      render: (tier: number) => <Tag>{`Tier ${tier}`}</Tag>,
    },
    {
      title: t('skills.status') || 'Status',
      dataIndex: 'is_enabled',
      key: 'is_enabled',
      width: 100,
      render: (enabled: boolean) => (
        <Tag color={enabled ? 'green' : 'default'}>
          {enabled ? t('skills.enabled') || 'Enabled' : t('skills.disabled') || 'Disabled'}
        </Tag>
      ),
    },
    {
      title: t('common.actions') || 'Actions',
      key: 'actions',
      width: 150,
      render: (_: any, record: ChatbotSkill) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<SettingOutlined />}
            onClick={() => handleOpenConfigModal(record)}
          >
            {t('common.configure') || 'Configure'}
          </Button>
          <Popconfirm
            title={t('skills.unassignConfirm') || 'Remove this skill?'}
            onConfirm={() => handleUnassignSkill(record.skill_id)}
            okText={t('common.yes') || 'Yes'}
            cancelText={t('common.no') || 'No'}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              {t('common.remove') || 'Remove'}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const unassignedSkills = availableSkills.filter(
    skill => !assignedSkills.some(as => as.skill_id === skill.skill_id)
  );

  return (
    <div>
      {!chatbotId && (
        <Alert
          message={t('skills.saveBotWarning') || 'Save the bot first to assign skills'}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Space style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleOpenAssignModal}
          disabled={!chatbotId || unassignedSkills.length === 0}
        >
          {t('skills.assignSkill') || 'Assign Skill'}
        </Button>
      </Space>

      <Table
        columns={columns}
        dataSource={assignedSkills}
        rowKey="chatbot_skill_id"
        loading={loading}
        pagination={false}
        locale={{
          emptyText: t('skills.noSkillsAssigned') || 'No skills assigned to this bot yet',
        }}
      />

      {/* Assign Skill Modal */}
      <Modal
        title={t('skills.assignSkill') || 'Assign Skill'}
        open={assignModalVisible}
        onOk={handleAssignSkill}
        onCancel={() => setAssignModalVisible(false)}
        okText={t('common.assign') || 'Assign'}
        cancelText={t('common.cancel') || 'Cancel'}
      >
        <Form form={assignForm} layout="vertical">
          <Form.Item
            name="skill_id"
            label={t('skills.selectSkill') || 'Select Skill'}
            rules={[{ required: true }]}
          >
            <Select
              placeholder={t('skills.selectSkillPlaceholder') || 'Choose a skill'}
              onChange={setSelectedSkillId}
            >
              {unassignedSkills.map(skill => (
                <Select.Option key={skill.skill_id} value={skill.skill_id}>
                  <Space direction="vertical" size={0}>
                    <Text strong dir="auto">{skill.skill_name}</Text>
                    {skill.skill_description && (
                      <Text type="secondary" style={{ fontSize: 11 }} dir="auto">
                        {skill.skill_description}
                      </Text>
                    )}
                  </Space>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="skill_priority"
            label={t('skills.priority') || 'Priority'}
            extra={t('skills.priorityHelp') || 'Lower number = higher priority'}
          >
            <InputNumber min={1} max={100} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="min_user_tier"
            label={t('skills.minTier') || 'Minimum User Tier'}
            extra={t('skills.minTierHelp') || 'Users below this tier cannot use this skill'}
          >
            <InputNumber min={1} max={5} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="is_enabled"
            label={t('skills.enabled') || 'Enabled'}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Configure Skill Modal */}
      <Modal
        title={t('skills.configureSkill') || 'Configure Skill'}
        open={configModalVisible}
        onOk={handleUpdateConfig}
        onCancel={() => setConfigModalVisible(false)}
        okText={t('common.save') || 'Save'}
        cancelText={t('common.cancel') || 'Cancel'}
      >
        <Form form={configForm} layout="vertical">
          <Form.Item
            name="skill_priority"
            label={t('skills.priority') || 'Priority'}
          >
            <InputNumber min={1} max={100} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="min_user_tier"
            label={t('skills.minTier') || 'Minimum User Tier'}
          >
            <InputNumber min={1} max={5} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="is_enabled"
            label={t('skills.enabled') || 'Enabled'}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
